import { useState } from "react";
import { Phone, MapPin, Clock, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { api, BUSINESS } from "../lib/api";
import { toast } from "sonner";
import { SEO } from "../components/SEO";

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We’ll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Sorry, something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO
        path="/contact"
        title="Contact Sixty Finance — Call 0431 550 780"
        description="Contact Sixty Finance. Call 0431 550 780, email dilraj@sixtyfinance.com or visit 5/10 Nova Court, Craigieburn VIC 3064. Monday–Friday 9am–5pm."
      />
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Contact</p>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-emerald-300">
            Let’s chat.
          </h1>
          <p className="mt-6 max-w-2xl text-emerald-100/85 text-base md:text-lg leading-relaxed">
            Prefer a call? We answer during business hours. Prefer email? Drop
            us a message and we’ll reply within 1 business day.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-5">
          <a
            href={BUSINESS.phoneHref}
            data-testid="contact-phone"
            className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-emerald-100 hover:shadow-lg transition-shadow"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-900 text-emerald-300">
              <Phone className="h-5 w-5" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Call us</div>
              <div className="mt-1 text-lg font-semibold num text-emerald-950">{BUSINESS.phone}</div>
              <div className="text-sm text-slate-500">{BUSINESS.hours}</div>
            </div>
          </a>
          <a
            href={`mailto:${BUSINESS.email}`}
            className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-emerald-100 hover:shadow-lg transition-shadow"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-900 text-emerald-300">
              <Mail className="h-5 w-5" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Email</div>
              <div className="mt-1 text-emerald-950 font-medium">{BUSINESS.email}</div>
            </div>
          </a>
          <div className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-emerald-100">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-900 text-emerald-300">
              <MapPin className="h-5 w-5" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Visit</div>
              <div className="mt-1 text-emerald-950 font-medium">{BUSINESS.address}</div>
              <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><Clock className="h-3.5 w-3.5" /> {BUSINESS.hours}</div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-emerald-100 shadow-sm">
            <iframe
              title="Sixty Finance location"
              src={`https://www.google.com/maps?q=${BUSINESS.mapEmbedQuery}&output=embed`}
              width="100%"
              height="260"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block"
            />
          </div>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div
              data-testid="contact-success"
              className="rounded-3xl bg-emerald-50 border border-emerald-200 p-10 text-center"
            >
              <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
              <h2 className="mt-4 text-2xl font-bold text-emerald-950">Message sent</h2>
              <p className="mt-2 text-emerald-800/80">Thanks — we’ll respond within one business day.</p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 px-5 py-2.5 rounded-full bg-emerald-900 text-white text-sm font-semibold"
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl bg-white p-8 md:p-10 border border-gray-100 shadow-sm"
            >
              <h2 className="text-2xl font-bold text-emerald-950">Send us a message</h2>
              <p className="mt-2 text-sm text-slate-500">We reply within 1 business day.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <FormField label="Full name" name="full_name" value={form.full_name} onChange={onChange} required />
                <FormField label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                <FormField label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} />
                <FormField label="Subject (optional)" name="subject" value={form.subject} onChange={onChange} />
              </div>
              <label className="mt-4 block">
                <span className="text-sm font-semibold text-emerald-950">Message</span>
                <textarea
                  data-testid="contact-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                  className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-0 outline-none"
                  placeholder="How can we help?"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800 disabled:opacity-60 transition-colors"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function FormField({ label, name, type = "text", value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-emerald-950">
        {label} {required && <span className="text-emerald-600">*</span>}
      </span>
      <input
        data-testid={`contact-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-emerald-950 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-0 outline-none"
      />
    </label>
  );
}
